import React from 'react'
import type { Placement } from '@floating-ui/react-dom'
import {
  arrow,
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from '@floating-ui/react-dom'

const getStaticSide = placement => {
  const sides = {
    top: 'bottom',
    right: 'left',
    bottom: 'top',
    left: 'right',
  }

  return sides[placement]
}

const getRotateAngle = placement => {
  const sides = {
    top: '180deg',
    right: '-90deg',
    bottom: '0',
    left: '90deg',
  }

  return sides[placement]
}

export const useFloatingElement = ({
  initialPlacement = 'bottom-end' as Placement,
  offsetSize = 12.5,
  hasArrow = false,
} = {}) => {
  const arrowRef = React.useRef<SVGSVGElement>(null)

  const { floating, middlewareData, placement, reference, strategy, x, y } =
    useFloating({
      // NOTE: In general, offset() should always go at the beginning of
      // the middleware array, while arrow() and hide() at the end. The
      // other core middleware can be shifted around depending on the
      // desired behavior.
      middleware: [
        offset(offsetSize),
        flip(),
        shift(),
        hasArrow && { ...arrow({ element: arrowRef }) },
      ],
      placement: initialPlacement,
      whileElementsMounted: autoUpdate,
    })

  const sidePrefix = placement.split('-')[0]

  const { x: arrowX, y: arrowY } = middlewareData?.arrow ?? {}

  const side = getStaticSide(sidePrefix)
  const angle = getRotateAngle(sidePrefix)
  const offsetX = `${arrowX}px`
  const offsetY = `${arrowY}px`
  const staticY = `${-(offsetSize + 1.5)}px`

  return {
    arrowRef,
    arrowStyle: {
      left: arrowX != null ? offsetX : '',
      top: arrowY != null ? offsetY : '',
      right: '',
      bottom: '',
      transform: `rotate(${angle})`,
      [side]: staticY,
    },
    floating,
    floatingStyle: {
      position: strategy,
      width: 'max-content',
      top: y ?? 0,
      left: x ?? 0,
    },
    middlewareData,
    reference,
  }
}
